import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { RootState } from '../../store';
import applicationApi from '../../services/api/application.api';
import { kubernetesApi } from '../../services/api/kubernetes.api';
import MainLayout from '../../components/layout/MainLayout';

interface Application {
  id: string;
  name: string;
  description: string;
  language: string;
  framework?: string;
  environment: string;
  api_key: string;
  status: string;
  created_at: string;
}

interface VerificationStatus {
  checking: boolean;
  success: boolean;
  message: string;
  lastCheck?: Date;
}

type CopiedField = 'apiKey' | 'installCommand' | null;

export default function KubernetesAgentInstallPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('agents');
  const user = useSelector((state: RootState) => state.auth.user);
  const [currentStep, setCurrentStep] = useState(1);
  const [existingApplications, setExistingApplications] = useState<Application[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<'helm' | 'kubectl'>('helm');
  const [apiKey, setApiKey] = useState<string>('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [copiedField, setCopiedField] = useState<CopiedField>(null);
  const [clusterName, setClusterName] = useState<string>('my-cluster');
  const [verification, setVerification] = useState<VerificationStatus>({
    checking: false,
    success: false,
    message: t('install.verification.noData'),
  });
  const [connectedClusters, setConnectedClusters] = useState<any[]>([]);

  useEffect(() => {
    loadExistingApplications();
    loadConnectedClusters();
    const interval = setInterval(loadConnectedClusters, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadExistingApplications = async () => {
    try {
      const response = await applicationApi.getAll();
      if (response.success && response.data) {
        // Show ALL active applications, don't filter by language
        const activeApps = response.data.filter((app) => app.status === 'active');
        setExistingApplications(activeApps);
        // DON'T auto-select first application - let user choose
      }
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleSelectApplication = (app: Application) => {
    setSelectedApplication(app);
    setApiKey(app.api_key);
  };

  const handleUseSelectedKey = () => {
    if (selectedApplication && apiKey) {
      setCurrentStep(2);
    }
  };

  const generateApiKey = async () => {
    setGeneratingKey(true);
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const response = await applicationApi.create({
        name: `Kubernetes Monitoring - ${timestamp}`,
        description: 'Application for Kubernetes cluster monitoring',
        language: 'kubernetes-agent',
        environment: 'production',
      });

      if (response.success && response.data) {
        setApiKey(response.data.api_key);
        setSelectedApplication(response.data);
        setCurrentStep(2);
      } else {
        throw new Error('Failed to create application');
      }
    } catch (error: any) {
      console.error('Failed to generate API key:', error);
      toast.error(error.response?.data?.error || t('kubernetes.errors.failedToGenerate'), {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setGeneratingKey(false);
    }
  };

  const loadConnectedClusters = async () => {
    try {
      const clusters = await kubernetesApi.getClusters();
      console.log('Loaded Kubernetes clusters:', clusters);
      if (clusters && Array.isArray(clusters)) {
        setConnectedClusters(clusters);
      }
    } catch (error) {
      console.error('Failed to load clusters:', error);
      setConnectedClusters([]);
    }
  };

  const verifyConnection = async () => {
    setVerification({ checking: true, success: false, message: t('install.verification.checking') });

    try {
      // Add minimum 2 second delay + actual API call
      const [, ] = await Promise.all([
        new Promise(resolve => setTimeout(resolve, 2000)),
        loadConnectedClusters()
      ]);

      console.log('Kubernetes verification - connected clusters:', connectedClusters);

      if (connectedClusters.length > 0) {
        setVerification({
          checking: false,
          success: true,
          message: t('install.verification.success'),
          lastCheck: new Date(),
        });
        setCurrentStep(4);
      } else {
        setVerification({
          checking: false,
          success: false,
          message: t('install.verification.noData'),
          lastCheck: new Date(),
        });
      }
    } catch (error: any) {
      console.error('Kubernetes verification error:', error);
      setVerification({
        checking: false,
        success: false,
        message: t('install.verification.notFound'),
        lastCheck: new Date(),
      });
    }
  };

  const copyToClipboard = async (text: string, field: CopiedField) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getHelmCommand = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';
    const tenantId = user?.tenantId || '';
    const cluster = clusterName || 'my-cluster';
    return `# Prerequisites: Helm and kubectl only!
helm version
kubectl version --client

# Add DevSkin Helm repository
helm repo add devskin https://devskin1.github.io/devskin-monitoring-kubernetes-agent
helm repo update

# Install with Helm (super simple!)
helm install devskin-k8s-agent devskin/devskin-kubernetes-agent \\
  --namespace devskin-monitoring \\
  --create-namespace \\
  --set apiKey="${apiKey}" \\
  --set tenantId="${tenantId}" \\
  --set serverUrl="${baseUrl}" \\
  --set clusterName="${cluster}"

# Check status
kubectl get pods -n devskin-monitoring
helm status devskin-k8s-agent -n devskin-monitoring

# Optional: Customize with values file
cat > values.yaml <<EOF
apiKey: "${apiKey}"
tenantId: "${tenantId}"
serverUrl: "${baseUrl}"
clusterName: "${cluster}"
environment: "production"
collectionInterval: 60000
resources:
  requests:
    memory: "256Mi"
    cpu: "200m"
EOF

helm upgrade devskin-k8s-agent devskin/devskin-kubernetes-agent \\
  -n devskin-monitoring -f values.yaml`;
  };

  const getKubectlCommand = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';
    const tenantId = user?.tenantId || '';
    const cluster = clusterName || 'my-cluster';
    return `# Prerequisites: kubectl only!
kubectl version --client

# Create namespace
kubectl create namespace devskin-monitoring

# Create Secret with API key
kubectl create secret generic devskin-k8s-agent \\
  --from-literal=api-key="${apiKey}" \\
  --from-literal=tenant-id="${tenantId}" \\
  --from-literal=server-url="${baseUrl}" \\
  -n devskin-monitoring

# Deploy the agent (using Docker Hub image!)
cat <<EOF | kubectl apply -f -
apiVersion: v1
kind: ServiceAccount
metadata:
  name: devskin-agent
  namespace: devskin-monitoring
---
apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRole
metadata:
  name: devskin-agent
rules:
- apiGroups: [""]
  resources: ["nodes", "pods", "services", "namespaces", "events"]
  verbs: ["get", "list", "watch"]
- apiGroups: ["apps"]
  resources: ["deployments", "daemonsets", "replicasets", "statefulsets"]
  verbs: ["get", "list", "watch"]
- apiGroups: ["metrics.k8s.io"]
  resources: ["nodes", "pods"]
  verbs: ["get", "list"]
---
apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRoleBinding
metadata:
  name: devskin-agent
roleRef:
  apiGroup: rbac.authorization.k8s.io
  kind: ClusterRole
  name: devskin-agent
subjects:
- kind: ServiceAccount
  name: devskin-agent
  namespace: devskin-monitoring
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: devskin-k8s-agent
  namespace: devskin-monitoring
  labels:
    app: devskin-k8s-agent
spec:
  replicas: 1
  selector:
    matchLabels:
      app: devskin-k8s-agent
  template:
    metadata:
      labels:
        app: devskin-k8s-agent
    spec:
      serviceAccountName: devskin-agent
      containers:
      - name: agent
        image: devskin/kubernetes-agent:latest
        env:
        - name: DEVSKIN_API_KEY
          valueFrom:
            secretKeyRef:
              name: devskin-k8s-agent
              key: api-key
        - name: DEVSKIN_TENANT_ID
          valueFrom:
            secretKeyRef:
              name: devskin-k8s-agent
              key: tenant-id
        - name: DEVSKIN_SERVER_URL
          valueFrom:
            secretKeyRef:
              name: devskin-k8s-agent
              key: server-url
        - name: CLUSTER_NAME
          value: "${cluster}"
        resources:
          requests:
            memory: "128Mi"
            cpu: "100m"
          limits:
            memory: "256Mi"
            cpu: "200m"
EOF

# Check status
kubectl get pods -n devskin-monitoring -l app=devskin-k8s-agent
kubectl logs -n devskin-monitoring -l app=devskin-k8s-agent`;
  };

  const getInstallCommand = () => {
    return selectedMethod === 'helm' ? getHelmCommand() : getKubectlCommand();
  };

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/agents')}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <span>☸️</span>
              <span>{t('kubernetes.title')}</span>
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {t('kubernetes.subtitle')}
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: t('install.steps.selectApiKey') },
              { num: 2, label: t('install.steps.installAgent') },
              { num: 3, label: t('install.steps.verifyConnection') },
              { num: 4, label: t('install.steps.complete') },
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      currentStep >= step.num
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {currentStep > step.num ? '✓' : step.num}
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                    {step.label}
                  </span>
                </div>
                {idx < 3 && (
                  <div
                    className={`h-1 flex-1 mx-4 ${
                      currentStep > step.num
                        ? 'bg-indigo-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Select or Generate API Key */}
        {currentStep === 1 && (
          <>
            {/* Prerequisites */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center space-x-2">
                <span>📋</span>
                <span>{t('kubernetes.prerequisites.title')}</span>
              </h3>
              <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-400">
                <li className="flex items-start space-x-2">
                  <span>✓</span>
                  <span>{t('kubernetes.prerequisites.cluster')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span>✓</span>
                  <span>{t('kubernetes.prerequisites.kubectl')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span>✓</span>
                  <span>{t('kubernetes.prerequisites.helm')}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span>✓</span>
                  <span>{t('kubernetes.prerequisites.metrics')}</span>
                </li>
              </ul>
            </div>

            {/* Existing Applications */}
            {loadingApplications ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">{t('install.apiKey.generating')}</p>
              </div>
            ) : existingApplications.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {t('install.steps.selectApiKey')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('install.apiKey.selectExistingDesc', { count: existingApplications.length })}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {existingApplications.map((app) => (
                    <button
                      key={app.id}
                      onClick={() => handleSelectApplication(app)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedApplication?.id === app.id
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{app.name}</h3>
                        {selectedApplication?.id === app.id && (
                          <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{app.description}</p>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                          {app.language}
                        </span>
                        {app.framework && (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                            {app.framework}
                          </span>
                        )}
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                          {app.environment}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                        {t('kubernetes.install.created')} {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                </div>
                {selectedApplication && (
                  <button
                    onClick={handleUseSelectedKey}
                    className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                  >
                    {t('install.apiKey.useSelected')}
                  </button>
                )}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {t('install.apiKey.orGenerateNew')}
                  </p>
                </div>
              </div>
            ) : null}

            {/* Generate New API Key */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              {existingApplications.length === 0 && !loadingApplications && (
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {t('install.steps.selectApiKey')}
                </h2>
              )}
              {existingApplications.length > 0 && (
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('install.apiKey.generateNew')}
                </h3>
              )}
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('kubernetes.subtitle')}
              </p>
              <button
                onClick={generateApiKey}
                disabled={generatingKey}
                className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingKey ? t('install.apiKey.generating') : t('install.apiKey.generateNew')}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Install Agent */}
        {currentStep === 2 && (
          <>
            {/* API Key Display */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('install.apiKey.title')}
              </h2>
              <div className="flex items-center space-x-2 mb-4">
                <code className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-900 rounded-lg text-sm font-mono text-gray-800 dark:text-gray-200 break-all">
                  {apiKey}
                </code>
                <button
                  onClick={() => copyToClipboard(apiKey, 'apiKey')}
                  className={`flex items-center px-3 py-1.5 text-sm rounded-md transition-all ${
                    copiedField === 'apiKey'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20'
                  }`}
                  title={t('install.copy.apiKey')}
                >
                  {copiedField === 'apiKey' ? (
                    <>
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('install.copy.copied')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {t('install.copy.copy')}
                    </>
                  )}
                </button>
              </div>
              <p className="text-sm text-yellow-600 dark:text-yellow-500">
                {t('install.apiKey.warning')}
              </p>
            </div>

            {/* Cluster Name Input */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('kubernetes.clusterName.title')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('kubernetes.clusterName.description')}
              </p>
              <input
                type="text"
                value={clusterName}
                onChange={(e) => setClusterName(e.target.value)}
                placeholder="production-cluster"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Installation Method Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('install.steps.installAgent')}
              </h2>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { id: 'helm', label: t('kubernetes.methods.helm.name'), icon: '⎈', description: t('kubernetes.methods.helm.description') },
                  { id: 'kubectl', label: t('kubernetes.methods.kubectl.name'), icon: '📜', description: t('kubernetes.methods.kubectl.description') },
                ].map((method) => (
                  <button
                    key={method.id}
                    onClick={() => setSelectedMethod(method.id as any)}
                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                      selectedMethod === method.id
                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="text-3xl">{method.icon}</div>
                      <div className="font-semibold text-gray-900 dark:text-white">{method.label}</div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{method.description}</p>
                  </button>
                ))}
              </div>

              {/* Installation Command */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t('install.steps.installAgent')}
              </h3>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm max-h-96">
                  <code>{getInstallCommand()}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(getInstallCommand(), 'installCommand')}
                  className={`absolute top-2 right-2 px-3 py-1.5 text-sm rounded transition-all ${
                    copiedField === 'installCommand'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  {copiedField === 'installCommand' ? `✓ ${t('install.copy.copied')}` : t('install.copy.copy')}
                </button>
              </div>

              {/* What Gets Monitored */}
              <div className="mt-6 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {t('kubernetes.monitoring.title')}
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <div>• {t('kubernetes.monitoring.items.cluster')}</div>
                  <div>• {t('kubernetes.monitoring.items.nodes')}</div>
                  <div>• {t('kubernetes.monitoring.items.pods')}</div>
                  <div>• {t('kubernetes.monitoring.items.deployments')}</div>
                  <div>• {t('kubernetes.monitoring.items.services')}</div>
                  <div>• {t('kubernetes.monitoring.items.pvc')}</div>
                  <div>• {t('kubernetes.monitoring.items.restarts')}</div>
                  <div>• {t('kubernetes.monitoring.items.events')}</div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  {t('install.back')}
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                >
                  {t('kubernetes.install.continueToVerification')}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Verify Connection */}
        {currentStep === 3 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('kubernetes.install.step3Title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('kubernetes.install.step3Description')}
            </p>

            {/* Verification Status - Enhanced UI */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg p-6 border-2 border-indigo-200 dark:border-indigo-800 mb-6">
              <div className="flex items-start space-x-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    verification.checking
                      ? 'bg-indigo-100 dark:bg-indigo-900/40'
                      : verification.success
                      ? 'bg-green-100 dark:bg-green-900/40'
                      : 'bg-yellow-100 dark:bg-yellow-900/40'
                  }`}
                >
                  {verification.checking ? (
                    <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : verification.success ? (
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {verification.checking
                      ? t('kubernetes.install.checkingConnection')
                      : verification.success
                      ? t('kubernetes.install.clusterConnected')
                      : t('kubernetes.install.noClusterDetected')}
                  </h3>
                  <p
                    className={`text-sm ${
                      verification.success
                        ? 'text-green-700 dark:text-green-300'
                        : verification.checking
                        ? 'text-indigo-700 dark:text-indigo-300'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {verification.message}
                  </p>
                  {verification.lastCheck && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {t('kubernetes.install.lastChecked')} {verification.lastCheck.toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Troubleshooting Commands */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
                {t('kubernetes.troubleshooting.title')}
              </h4>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-yellow-800 dark:text-yellow-400 mb-1">{t('kubernetes.troubleshooting.checkPods')}</p>
                  <code className="block bg-yellow-100 dark:bg-yellow-900/50 p-2 rounded text-xs">
                    kubectl get pods -n devskin-system
                  </code>
                </div>
                <div>
                  <p className="text-yellow-800 dark:text-yellow-400 mb-1">{t('kubernetes.troubleshooting.viewLogs')}</p>
                  <code className="block bg-yellow-100 dark:bg-yellow-900/50 p-2 rounded text-xs">
                    kubectl logs -n devskin-system -l app=devskin-k8s-agent --tail=50
                  </code>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                {t('install.back')}
              </button>
              <button
                onClick={verifyConnection}
                disabled={verification.checking}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verification.checking ? t('kubernetes.install.checking') : t('kubernetes.install.verifyConnection')}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {currentStep === 4 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('kubernetes.complete.title')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {t('kubernetes.complete.message')}
              </p>
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={() => navigate('/infrastructure/kubernetes')}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                >
                  {t('kubernetes.complete.viewClusters')}
                </button>
                <button
                  onClick={() => navigate('/agents')}
                  className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg"
                >
                  {t('kubernetes.complete.backToAgents')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
